import React from 'react';
import { useSelector } from 'react-redux';
import styled from '@emotion/styled';
import InputLabel from './InputLabel';
import Autocomplete from './AutocompleteMain';
import Optional from './Optional';
import UsernameInputV2 from './UsernameInputV2';
import { FIRSTNAME_FIRST_LETTER_VALIDATOR, FIRSTNAME_VALIDATOR } from '../config/constants';
import { RootState } from '../store';
import breakpoints from '../theme/material/breakpoints';
import { isDarkMode } from 'utils/helpers';
import SecondaryButton from 'atoms/buttons/SecondaryButton';
import PrimaryButton from 'atoms/buttons/PrimaryButton';
import { SvgIcon } from '@material-ui/core';
import { CloseOutlined } from '@material-ui/icons';
import { getInputTheme } from 'style';

const AutocompleteContainer = styled('div')({
  maxWidth: '476px',
});

const ButtonContainer = styled('div')({
  width: '100%',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  [breakpoints.down(620.05)]: {
    marginBottom: 15,
  },
});

const EditProfileCoverContainer = styled('div')({
  paddingTop: 5,
  paddingRight: 0,
  paddingLeft: 15,
  flex: 3,
  maxWidth: '100%',
  flexDirection: 'column',
  [breakpoints.down(620.05)]: {
    paddingTop: 16,
    maxWidth: '100%',
    paddingRight: 15,
    paddingLeft: 15,
  },
});

const EditableProfileCover = (props: any): JSX.Element => {
  const {
    errors,
    displayName,
    myLocation,
    handleSubmit,
    handleCancel,
    updateUsername,
    onChangeAddress,
    onSelectLocation,
    isEditableName,
  } = props;

  const { isUpdatingProfileCover, username } = useSelector((state: RootState) => state.profile);

  const locationInputProps = {
    value: myLocation,
    id: 'autocompleteLocation',
    placeholder: 'Enter a Location',
    type: 'text',
    onChange: onChangeAddress,
    status: errors && errors.city && 'error',
    statusMessage: errors && errors.city,
  };

  const checkEmptyandError = (myName: string, loc: string) => {
    const isValidStartLetter = FIRSTNAME_FIRST_LETTER_VALIDATOR.test(myName);
    const isValidLetter = myName.match(FIRSTNAME_VALIDATOR);
    const isValidUsername = isEditableName ? username?.toLowerCase() !== myName?.toLowerCase() : true;

    return (
      myName &&
      loc &&
      !(myName.length < 3 || myName.length > 16) &&
      isValidStartLetter &&
      isValidLetter &&
      isValidUsername
    );
  };

  const locationEmpty = (loc: string) => {
    return loc !== undefined && loc.length === 0;
  };

  return (
    <EditProfileCoverContainer>
      {isEditableName && (
        <UsernameInputV2
          inputLabel="First Name / Nickname"
          value={displayName}
          onChange={updateUsername}
          customStyleLabel={{
            fontWeight: 600,
            fontSize: 14,
            lineHeight: '19px',
            letterSpacing: -0.3,
            marginRight: 4,
            color: isDarkMode() ? '#F4F4F4' : '#595959',
          }}
          customInputStyle={{ color: getInputTheme().textInput, lineHeight: '19px' }}
          customStyle={{ marginRight: 0, maxWidth: '100%' }}
          autoFocus
          showInfoIcon
          customStyleLabelContainer={{ display: 'flex', alignItems: 'center' }}
          customStatusDescriptionStyle={{ fontSize: 14, lineHeight: '19px', letterSpacing: -0.3 }}
        />
      )}
      <AutocompleteContainer style={{ maxWidth: '100%' }}>
        <InputLabel
          // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
          eleRef={r => {
            // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
            this._city = r;
          }}
          label="Location"
          customStyle={{
            fontWeight: 600,
            fontSize: 14,
            lineHeight: '19px',
            letterSpacing: -0.3,
            color: isDarkMode() ? '#F4F4F4' : '#595959',
          }}
        />
        <Autocomplete
          inputProps={locationInputProps}
          onSelect={onSelectLocation}
          error={errors && errors.city}
          status={errors && errors.city && 'error'}
          statusMessage={errors && errors.city}
          customInputStyle={{
            fontSize: 14,
            color: getInputTheme().textInput,
            lineHeight: '19px',
            paddingRight: '60px !important',
          }}
          emptyLocation={locationEmpty(myLocation)}
          customStyle={{ marginRight: 0 }}
          isEditProfile
        />
      </AutocompleteContainer>
      <ButtonContainer>
        <Optional show={!isUpdatingProfileCover}>
          <SecondaryButton
            size="x-small"
            color="blue"
            onClick={handleCancel}
            data-test-id="profile-cover-cancel-btn"
            label="Cancel"
            withIcon={<SvgIcon component={CloseOutlined} style={{ width: 16, height: 16, cursor: 'pointer' }} />}
            customStyle={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '4px',
              width: '100px',
              lineHeight: '16px',
            }}
          />
        </Optional>

        <PrimaryButton
          color="red"
          size="x-small"
          data-test-id="profile-cover-save-btn"
          onClick={handleSubmit}
          label={isUpdatingProfileCover ? 'Saving...' : 'Save'}
          customStyle={{ width: 'max-content', borderRadius: '4px', marginLeft: 'auto' }}
          disabled={!checkEmptyandError(displayName, myLocation) || isUpdatingProfileCover}
        />
      </ButtonContainer>
    </EditProfileCoverContainer>
  );
};

export default React.memo(EditableProfileCover);
